import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTrigger,
    Heading,
    Label,
    ListBox,
    ListBoxItem,
    Modal,
    Popover,
    Select,
    SelectValue,
} from "react-aria-components";
import { msgid, ngettext, t } from "ttag";

import type {
    ServerCmsTypesCategoryNavigation as CategoryNavigation,
    ServerCmsModelsPagesCategoryPageFacetValueGridPromo as FacetValueGridPromo,
    ServerCmsTypesFilterFacet as FilterFacet,
    GridPromoPlacements,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

import { ReactivatedContext } from "../../context";
import { Themed } from "../../styles/themes";
import { ButtonPrimary, Clickable } from "../Clickables";
import { GridPromoCard } from "../GridPromoCard";
import { ProductCard } from "../ProductCard";
import { SubcategoryTiles } from "../SubcategoryTiles";
import { Svg } from "../Svg";
import { FilterChips } from "./FilterChips";
import { FilterSidebar, MobileFilterModal } from "./FilterControls";
import type { SelectedFilters } from "./filterProducts";
import { filterProducts, recalculateFacetCounts } from "./filterProducts";
import {
    type SortOption,
    buildFilterURL,
    getQualifierLabel,
    getTitleQualification,
    selectGridItems,
    sortProducts,
} from "./utils";

import styles from "./index.module.scss";

interface MobileSortModalProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    sortBy: SortOption;
    setSortBy: (value: SortOption) => void;
    sortOptions: { id: SortOption; label: string }[];
}

const MobileSortModal = ({
    isOpen,
    onOpenChange,
    sortBy,
    setSortBy,
    sortOptions,
}: MobileSortModalProps) => (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal className={styles.sortModal}>
            <Dialog>
                {({ close }) => (
                    <Themed
                        theme="offWhite"
                        className={styles.sortModalWrapper}
                    >
                        <div className={styles.sortModalHeader}>
                            <Heading
                                slot="title"
                                className={styles.sortModalTitle}
                            >
                                {t`Sort by`}
                            </Heading>
                            <Clickable
                                onPress={close}
                                className={styles.sortModalClose}
                            >
                                <Svg
                                    name="x"
                                    className={styles.sortModalCloseIcon}
                                    visuallyHiddenText={t`Close`}
                                />
                            </Clickable>
                        </div>
                        <ListBox
                            className={styles.sortModalList}
                            aria-label={t`Sort by`}
                            selectionMode="single"
                            selectedKeys={[sortBy]}
                            onSelectionChange={(keys) => {
                                const key = [...keys][0] as SortOption;
                                /* v8 ignore else -- @preserve */
                                if (key) {
                                    setSortBy(key);
                                    close();
                                }
                            }}
                        >
                            {sortOptions.map((option) => (
                                <ListBoxItem
                                    key={option.id}
                                    id={option.id}
                                    className={styles.sortModalItem}
                                    textValue={option.label}
                                >
                                    {option.label}
                                    <Svg
                                        name="check"
                                        className={styles.sortModalItemCheck}
                                        aria-hidden="true"
                                    />
                                </ListBoxItem>
                            ))}
                        </ListBox>
                    </Themed>
                )}
            </Dialog>
        </Modal>
    </DialogTrigger>
);

export interface CategoryContentBlockProps {
    categoryTitle: string;
    categoryNavigation: CategoryNavigation;
    filterFacets: FilterFacet[];
    products: ProductSummary[];
    gridPromos: { value: GridPromoPlacements };
    facetValueGridPromos: FacetValueGridPromo[];
    selectedFilters: SelectedFilters;
    setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>;
}

export const CategoryContentBlock = ({
    categoryTitle,
    categoryNavigation,
    filterFacets,
    products,
    gridPromos,
    facetValueGridPromos,
    selectedFilters,
    setSelectedFilters,
}: CategoryContentBlockProps) => {
    const { request } = useContext(ReactivatedContext);

    const [sortBy, setSortBy] = useState<SortOption>("featured");
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);

    const isInitialRender = useRef(true);
    useEffect(() => {
        window.history.replaceState(
            null,
            "",
            buildFilterURL(request.url, selectedFilters, filterFacets),
        );

        if (isInitialRender.current) {
            isInitialRender.current = false;
        } else {
            const rect = sectionRef.current?.getBoundingClientRect();
            if (rect && rect.top < 0) {
                sectionRef.current?.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [selectedFilters, request.url, filterFacets]);

    const filteredProducts = filterProducts(products, selectedFilters);
    const sortedProducts = sortProducts(filteredProducts, sortBy);
    const gridItems = selectGridItems(
        sortedProducts,
        gridPromos.value,
        facetValueGridPromos,
        selectedFilters,
    );
    const adjustedFacets = recalculateFacetCounts(
        filterFacets,
        products,
        selectedFilters,
    );
    const activeFilterCount = Object.values(selectedFilters).reduce(
        (sum, values) => sum + values.length,
        0,
    );
    const hasActiveFilters = activeFilterCount > 0;

    const qualification = getTitleQualification(filterFacets, selectedFilters);
    const productCount = filteredProducts.length;

    const defaultExpandedKeys = filterFacets.map((facet) => facet.slug);

    const clearAllFilters = () => setSelectedFilters({});

    const sortOptions: { id: SortOption; label: string }[] = [
        { id: "featured", label: t`Featured` },
        { id: "price-asc", label: t`Price - Low to High` },
        { id: "price-desc", label: t`Price - High to Low` },
    ];

    const theme = "offWhite";

    return (
        <Themed theme={theme}>
            {categoryNavigation.has_subcategories && (
                <SubcategoryTiles
                    tiles={categoryNavigation.items}
                    categoryTitle={categoryTitle}
                />
            )}
            <section ref={sectionRef} className={styles.productGrid}>
                <div className={styles.grid}>
                    <header className={styles.gridHeader}>
                        <div className={styles.gridTitleWrapper}>
                            <h1 className={styles.gridTitle}>
                                {qualification
                                    ? `${getQualifierLabel(qualification)} ${categoryTitle}`
                                    : t`All ${categoryTitle}`}
                            </h1>
                            <p
                                className={styles.productCount}
                                aria-live="polite"
                            >
                                {ngettext(
                                    msgid`${productCount} product`,
                                    `${productCount} products`,
                                    productCount,
                                )}
                            </p>
                        </div>
                        <Select
                            className={styles.sortSelect}
                            value={sortBy}
                            onChange={(key) => setSortBy(key as SortOption)}
                        >
                            <Label className={styles.sortLabel}>
                                {t`Sort by`}:
                            </Label>
                            <Clickable className={styles.sortButton}>
                                <SelectValue />
                                <Svg
                                    name="caret-right"
                                    className={styles.sortCaret}
                                    aria-hidden="true"
                                />
                            </Clickable>
                            <Popover className={styles.sortPopover}>
                                <Themed theme={theme}>
                                    <ListBox>
                                        {sortOptions.map((option) => (
                                            <ListBoxItem
                                                key={option.id}
                                                id={option.id}
                                                className={
                                                    styles.sortListBoxItem
                                                }
                                                textValue={option.label}
                                            >
                                                {option.label}
                                            </ListBoxItem>
                                        ))}
                                    </ListBox>
                                </Themed>
                            </Popover>
                        </Select>
                    </header>
                    <div className={styles.mobileActions}>
                        <Clickable
                            onPress={() => setIsFilterModalOpen(true)}
                            className={styles.mobileActionsButton}
                        >
                            {t`Filters`}
                            {activeFilterCount > 0 && ` (${activeFilterCount})`}
                            <Svg
                                name="filter"
                                className={styles.mobileFilterSvg}
                                aria-hidden="true"
                            />
                        </Clickable>
                        <Clickable
                            onPress={() => setIsSortModalOpen(true)}
                            className={styles.mobileActionsButton}
                        >
                            {t`Sort by`}
                            <Svg
                                name="caret-right"
                                className={styles.mobileSortSvg}
                                aria-hidden="true"
                            />
                        </Clickable>
                    </div>
                    <MobileSortModal
                        isOpen={isSortModalOpen}
                        onOpenChange={setIsSortModalOpen}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortOptions={sortOptions}
                    />
                    <MobileFilterModal
                        isOpen={isFilterModalOpen}
                        onOpenChange={setIsFilterModalOpen}
                        categoryNavigation={categoryNavigation}
                        currentPath={request.path}
                        hasActiveFilters={hasActiveFilters}
                        clearAllFilters={clearAllFilters}
                        defaultExpandedKeys={defaultExpandedKeys}
                        adjustedFacets={adjustedFacets}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                    />
                    {hasActiveFilters && (
                        <FilterChips
                            filterFacets={filterFacets}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            clearAllFilters={clearAllFilters}
                        />
                    )}
                    {filteredProducts.length === 0 && (
                        <div className={styles.emptyState}>
                            <p>{t`No products match your filters.`}</p>
                            <ButtonPrimary onPress={clearAllFilters}>
                                {t`Clear All Filters`}
                            </ButtonPrimary>
                        </div>
                    )}
                    {gridItems.length > 0 && (
                        <ul role="list" className={styles.productList}>
                            {gridItems.map((item) =>
                                item.type === "product" ? (
                                    <ProductCard
                                        tag="li"
                                        key={item.product.sku}
                                        product={item.product}
                                        layout="vertical"
                                    />
                                ) : (
                                    <GridPromoCard
                                        key={item.id}
                                        value={item.snippet}
                                    />
                                ),
                            )}
                        </ul>
                    )}
                </div>
                <FilterSidebar
                    categoryNavigation={categoryNavigation}
                    currentPath={request.path}
                    hasActiveFilters={hasActiveFilters}
                    clearAllFilters={clearAllFilters}
                    defaultExpandedKeys={defaultExpandedKeys}
                    adjustedFacets={adjustedFacets}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                />
            </section>
        </Themed>
    );
};
